const {
  EINVOICE_UPDATE_DRAFT,
  EINVOICE_PREVIEW_CALCULATION,
  EINVOICE_VALIDATE_DRAFT,
  EINVOICE_ISSUE,
} = require('@/core/services/store/modules/einvoice/einvoice.module')

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeImageUrl(...values) {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }

    const trimmed = value.trim()
    if (trimmed) {
      return trimmed
    }
  }

  return null
}

function roundAmount(value) {
  return Math.round(toNumber(value, 0))
}

function computeLineGrossAmount(line = {}) {
  return roundAmount(toNumber(line.quantity, 0) * toNumber(line.unitPrice, 0))
}

function computeLineNetAmount(line = {}) {
  return Math.max(0, computeLineGrossAmount(line) - roundAmount(line.discountAmount))
}

function computeLineTaxAmount(line = {}) {
  const taxRate = toNumber(line.taxRate, 0)
  if (taxRate < 0) {
    return 0
  }

  return roundAmount((computeLineNetAmount(line) * taxRate) / 100)
}

function computeDraftTotals(lineItems = []) {
  return lineItems.reduce(
    (totals, line) => {
      const grossAmount = computeLineGrossAmount(line)
      const netAmount = computeLineNetAmount(line)
      const taxAmount = computeLineTaxAmount(line)

      return {
        grossAmount: totals.grossAmount + grossAmount,
        totalBeforeTax: totals.totalBeforeTax + netAmount,
        vatAmount: totals.vatAmount + taxAmount,
        totalAmount: totals.totalAmount + netAmount + taxAmount,
      }
    },
    {
      grossAmount: 0,
      totalBeforeTax: 0,
      vatAmount: 0,
      totalAmount: 0,
    },
  )
}

function normalizeEditLineItem(line = {}, index = 0) {
  const imageUrl = normalizeImageUrl(line.imageUrl, line.productImageUrl)

  const normalizedLine = {
    lineNumber: line.lineNumber || line.lineId || index + 1,
    processType: line.processType || 1,
    productCode: line.productCode || line.itemCode || '',
    itemCode: line.itemCode || line.productCode || '',
    productName: line.productName || line.itemName || '',
    itemName: line.itemName || line.productName || '',
    unit: line.unit || line.unitOfMeasure || '',
    unitOfMeasure: line.unitOfMeasure || line.unit || '',
    quantity: toNumber(line.quantity, 1) || 1,
    unitPrice: toNumber(line.unitPrice, 0),
    discountRate: toNumber(line.discountRate, 0),
    discountAmount: roundAmount(
      line.discountAmount !== undefined ? line.discountAmount : line.discount,
    ),
    taxRate: line.taxRate !== undefined ? toNumber(line.taxRate, 10) : 10,
    taxAmount: 0,
    isBonus: Boolean(line.isBonus),
    billItemId: line.billItemId || null,
    productId: line.productId || null,
    imageUrl,
  }

  normalizedLine.taxAmount =
    line.taxAmount !== undefined || line.vatAmount !== undefined
      ? roundAmount(line.taxAmount !== undefined ? line.taxAmount : line.vatAmount)
      : computeLineTaxAmount(normalizedLine)

  return normalizedLine
}

function normalizeCanonicalNullableValue(value) {
  if (value === null || value === undefined) {
    return null
  }

  const normalized = typeof value === 'string' ? value.trim() : String(value).trim()
  return normalized || null
}

function normalizeCanonicalExchangeRate(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeCanonicalBuyerType(form = {}) {
  const explicitBuyerType = normalizeCanonicalNullableValue(form.buyerType)
  if (explicitBuyerType) {
    const normalizedExplicit = explicitBuyerType.toUpperCase()
    if (normalizedExplicit === 'ENTERPRISE' || normalizedExplicit === 'INDIVIDUAL') {
      return normalizedExplicit
    }
  }

  return normalizeCanonicalNullableValue(form.buyerTaxCode || form.customerTaxCode)
    ? 'ENTERPRISE'
    : 'INDIVIDUAL'
}

function buildEditDraftPayload(form = {}) {
  return {
    buyerType: normalizeCanonicalBuyerType(form),
    customerName: normalizeCanonicalNullableValue(
      form.buyerName !== undefined ? form.buyerName : form.customerName,
    ),
    contactPersonName: normalizeCanonicalNullableValue(form.contactPersonName),
    customerTaxCode: normalizeCanonicalNullableValue(
      form.buyerTaxCode !== undefined ? form.buyerTaxCode : form.customerTaxCode,
    ),
    customerAddress: normalizeCanonicalNullableValue(
      form.buyerAddress !== undefined ? form.buyerAddress : form.customerAddress,
    ),
    customerEmail: normalizeCanonicalNullableValue(
      form.buyerEmail !== undefined ? form.buyerEmail : form.customerEmail,
    ),
    customerPhone: normalizeCanonicalNullableValue(
      form.buyerPhone !== undefined ? form.buyerPhone : form.customerPhone,
    ),
    customerIdNumber: normalizeCanonicalNullableValue(
      form.buyerIdNumber !== undefined ? form.buyerIdNumber : form.customerIdNumber,
    ),
    paymentMethod: normalizeCanonicalNullableValue(form.paymentMethod),
    note: normalizeCanonicalNullableValue(form.note),
    currency: normalizeCanonicalNullableValue(form.currency) || 'VND',
    exchangeRate: normalizeCanonicalExchangeRate(form.exchangeRate),
  }
}

async function runSaveAndIssueFlow({
  dispatch,
  salesInvoiceId,
  saleType,
  payload,
  onStageChange = null,
}) {
  const runStage = async (stage, action, payloadArg) => {
    if (onStageChange) onStageChange(stage)
    try {
      return await dispatch(action, payloadArg)
    } catch (error) {
      error.flowStage = stage
      throw error
    }
  }

  await runStage('save', EINVOICE_UPDATE_DRAFT, {
    salesInvoiceId,
    saleType,
    payload,
  })

  await runStage('preview', EINVOICE_PREVIEW_CALCULATION, {
    saleType,
    invoices: [
      {
        salesInvoiceId,
        override: payload,
      },
    ],
  })

  const validationResult = await runStage('validate', EINVOICE_VALIDATE_DRAFT, {
    salesInvoiceId,
    saleType,
    payload,
  })

  if (validationResult && validationResult.isValid === false) {
    const validationError = new Error(validationResult.message || 'Draft validation failed')
    validationError.validationResult = validationResult
    validationError.flowStage = 'validate'
    throw validationError
  }

  return runStage('issue', EINVOICE_ISSUE, {
    salesInvoiceId,
    saleType,
  })
}

module.exports = {
  buildEditDraftPayload,
  computeDraftTotals,
  computeLineGrossAmount,
  computeLineNetAmount,
  computeLineTaxAmount,
  normalizeEditLineItem,
  runSaveAndIssueFlow,
}
